import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import type {
  LocationSearchAddress,
  LocationSearchResult,
} from '../../Store/StudentSlices/locationSearchSlice';

type LocationOption = LocationSearchResult | string;

const locationFilterOptions = createFilterOptions<LocationOption>();

const formatLocationAddress = (address?: LocationSearchAddress) =>
  [address?.municipality, address?.countrySubdivision, address?.country]
    .filter(Boolean)
    .join(', ');

const formatLocationOptionLabel = (option: LocationOption) =>
  typeof option === 'string' ? option : formatLocationAddress(option.address);

const getLocationOptionLabel = (option: LocationOption, fallback: string) =>
  formatLocationOptionLabel(option) || fallback;

type LocationSearchFieldProps = {
  value: string;
  options: LocationSearchResult[];
  placeholder?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  loading?: boolean;
};

const LocationSearchField = ({
  value,
  options,
  placeholder,
  onChange,
  disabled,
  loading,
}: LocationSearchFieldProps) => (
  <Autocomplete<LocationOption, false, false, true>
    freeSolo
    options={options}
    value={value}
    inputValue={value}
    disabled={disabled}
    loading={loading}
    loadingText="Searching..."
    isOptionEqualToValue={(option, current) =>
      formatLocationOptionLabel(option).toLowerCase() ===
      formatLocationOptionLabel(current).toLowerCase()
    }
    getOptionLabel={(option) => getLocationOptionLabel(option, value)}
    filterOptions={(items, params) => {
      const filtered = locationFilterOptions(items, params);
      const inputValue = params.inputValue.trim();
      const exists = items.some(
        (item) =>
          formatLocationOptionLabel(item).toLowerCase() ===
          inputValue.toLowerCase()
      );
      if (inputValue && !exists) {
        filtered.push(inputValue);
      }
      return filtered;
    }}
    onInputChange={(_, newInputValue) => onChange(newInputValue)}
    onChange={(_, newValue) => {
      if (typeof newValue === 'string') {
        onChange(newValue);
        return;
      }
      if (newValue) {
        onChange(getLocationOptionLabel(newValue, value));
        return;
      }
      onChange('');
    }}
    renderInput={(params) => (
      <TextField
        {...params}
        placeholder={placeholder}
        size="small"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            backgroundColor: '#fff',
            minHeight: '44px',
            paddingRight: '8px',
          },
          '& .MuiOutlinedInput-input': {
            padding: '10px 14px',
            fontSize: '14px',
            color: '#475569',
          },
          '& .MuiOutlinedInput-input::placeholder': {
            color: '#94A3B8',
            opacity: 1,
          },
          '.MuiOutlinedInput-notchedOutline': {
            borderColor: '#E2E8F0',
          },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
            {
              borderColor: '#2563EB',
            },
          '& .MuiSvgIcon-root': {
            color: '#94A3B8',
          },
        }}
      />
    )}
    renderOption={(props, option) => (
      <li {...props}>{getLocationOptionLabel(option, value)}</li>
    )}
    slotProps={{
      popper: {
        sx: {
          '&[data-popper-placement*="bottom"] .MuiAutocomplete-paper': {
            marginTop: '8px',
          },
          '&[data-popper-placement*="top"] .MuiAutocomplete-paper': {
            marginBottom: '8px',
          },
        },
      },
    }}
    sx={{
      '& .MuiAutocomplete-paper': {
        borderRadius: '10px',
        border: '1px solid #E2E8F0',
        boxShadow: '0 16px 32px -20px rgba(15, 23, 42, 0.4)',
        overflow: 'hidden',
      },
      '& .MuiAutocomplete-listbox': {
        padding: 0,
      },
      '& .MuiAutocomplete-option': {
        padding: '14px 16px',
        fontSize: '14px',
        color: '#64748B',
        borderBottom: '1px solid #F1F5F9',
        '&[aria-selected="true"]': {
          backgroundColor: '#F1F5F9',
        },
        '&.Mui-focused': {
          backgroundColor: '#F8FAFC',
        },
        '&:last-of-type': {
          borderBottom: 'none',
        },
      },
    }}
  />
);

export default LocationSearchField;
